import { Scene, PerspectiveCamera, WebGLRenderer, Matrix4, Vector3, Group, Quaternion, Object3D, Box3, sRGBEncoding, UnsignedByteType, Mesh, PMREMGenerator, MeshStandardMaterial, TextureLoader, InstancedMesh, ShaderMaterial, PlaneGeometry, WebGLRenderTarget, MeshBasicMaterial, OrthographicCamera, MeshDepthMaterial, Shader, DataTexture, Clock } from 'three'
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import { Font, FontLoader } from 'three/examples/jsm/loaders/FontLoader'
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry'
import { HorizontalBlurShader } from 'three/examples/jsm/shaders/HorizontalBlurShader.js';
import { VerticalBlurShader } from 'three/examples/jsm/shaders/VerticalBlurShader.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import Stats from 'three/examples/jsm/libs/stats.module.js';
import { GPUStatsPanel } from 'three/examples/jsm/utils/GPUStatsPanel.js';

import { addUserSelected, User, onResizeHead } from './users';

const SHOW_STATS = false

var CopyShader = {

  uniforms: {

    'tDiffuse': { value: null },
    'opacity': { value: 2.0 }

  },

  vertexShader: /* glsl */`
		varying vec2 vUv;
		void main() {
			vUv = uv;
			gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
		}`,

  fragmentShader: /* glsl */`
		uniform float opacity;
		uniform sampler2D tDiffuse;
		varying vec2 vUv;
		void main() {
			vec4 texel = texture2D( tDiffuse, vUv );
      float dx = vUv.x - 0.5;
      float dy = vUv.y - 0.5;
      float vig = dx * dx + dy * dy;

      if(vig < 0.25) {
        vig = 1.0-pow(vig * 4.0, 4.8);
      }else {
        vig = 0.0;
      }

      gl_FragColor = LinearTosRGB(vec4(texel.rgb, texel.a * vig));
      //gl_FragColor = vec4(vig, vig, vig, 1);
    }`

};

const clock = new Clock();

const init = async () => {
  const elem = document.getElementById('coin')

  if (!elem) {
    return
  }

  const scene = new Scene();
  // scene.background = new Color(0x0000ff)

  const camera = new PerspectiveCamera(50, elem.clientWidth / elem.clientHeight, 0.1, 100);
  camera.position.z = 1

  const renderer = new WebGLRenderer({ alpha: true, premultipliedAlpha: false });
  renderer.setSize(elem.clientWidth, elem.clientHeight);
  renderer.outputEncoding = sRGBEncoding
  renderer.physicallyCorrectLights = true
  renderer.setPixelRatio(window.devicePixelRatio * 2);
  elem.appendChild(renderer.domElement)

  // the container, if you need to move the plane just move this
  const shadowGroup = new Group();
  shadowGroup.position.y = -0.4;
  scene.add(shadowGroup);

  // the render target that will show the shadows in the plane texture
  const renderTarget = new WebGLRenderTarget(512, 512);
  renderTarget.texture.generateMipmaps = false;

  // the render target that we will use to blur the first render target
  const renderTargetBlur = new WebGLRenderTarget(512, 512);
  renderTargetBlur.texture.generateMipmaps = false;

  // make a plane and make it face up
  const planeGeometry = new PlaneGeometry(2.5, 2.5).rotateX(Math.PI / 2);
  const planeMaterial = new MeshBasicMaterial({
    map: renderTarget.texture,
    opacity: 1,
    transparent: true,
    depthWrite: false,
  });
  const plane = new Mesh(planeGeometry, planeMaterial);
  // make sure it's rendered after the fillPlane
  plane.renderOrder = 1;
  shadowGroup.add(plane);

  // the y from the texture is flipped!
  plane.scale.y = -1;

  // the plane onto which to blur the texture
  const blurPlane = new Mesh(planeGeometry);
  blurPlane.visible = false;
  shadowGroup.add(blurPlane);

  // the plane with the color of the ground
  const fillPlaneMaterial = new MeshBasicMaterial({
    color: 0xffffff,
    opacity: 0,
    transparent: true,
    depthWrite: false,
  });
  const fillPlane = new Mesh(planeGeometry, fillPlaneMaterial);
  fillPlane.rotateX(Math.PI);
  shadowGroup.add(fillPlane);

  // the camera to render the depth material from
  const shadowCamera = new OrthographicCamera(-2.5 / 2, 2.5 / 2, 2.5 / 2, - 2.5 / 2, 0, 1);
  shadowCamera.rotation.x = Math.PI / 2; // get the camera to look up
  shadowGroup.add(shadowCamera);

  // const cameraHelper = new CameraHelper(shadowCamera);
  // scene.add(cameraHelper)

  // like MeshDepthMaterial, but goes from black to transparent
  const depthMaterial = new MeshDepthMaterial();
  depthMaterial.userData.darkness = { value: 10 };
  depthMaterial.onBeforeCompile = (shader: Shader) => {
    shader.uniforms.darkness = depthMaterial.userData.darkness;
    shader.fragmentShader = /* glsl */`
      uniform float darkness;
      ${shader.fragmentShader.replace(
      'gl_FragColor = vec4( vec3( 1.0 - fragCoordZ ), opacity );',
      'gl_FragColor = vec4( vec3( 0.0 ), ( 1.0 - fragCoordZ ) * darkness );'
    )}
    `;
  };
  depthMaterial.depthTest = false;
  depthMaterial.depthWrite = false;

  const horizontalBlurMaterial = new ShaderMaterial(HorizontalBlurShader);
  horizontalBlurMaterial.depthTest = false;

  const verticalBlurMaterial = new ShaderMaterial(VerticalBlurShader);
  verticalBlurMaterial.depthTest = false;

  const blurShadow = (amount: number) => {
    blurPlane.visible = true;

    // blur horizontally and draw in the renderTargetBlur
    blurPlane.material = horizontalBlurMaterial;
    blurPlane.material.uniforms.tDiffuse.value = renderTarget.texture;
    horizontalBlurMaterial.uniforms.h.value = amount * 1 / 256;

    renderer.setRenderTarget(renderTargetBlur);
    renderer.render(blurPlane, shadowCamera);

    // blur vertically and draw in the main renderTarget
    blurPlane.material = verticalBlurMaterial;
    blurPlane.material.uniforms.tDiffuse.value = renderTargetBlur.texture;
    verticalBlurMaterial.uniforms.v.value = amount * 1 / 256;

    renderer.setRenderTarget(renderTarget);
    renderer.render(blurPlane, shadowCamera);

    blurPlane.visible = false;
  }

  // let axesHelper = new AxesHelper(100)
  // axesHelper.renderOrder = 999
  // axesHelper.onBeforeRender = (renderer) => renderer.clearDepth()
  // scene.add(axesHelper)

  // const helper = new CameraHelper( camera );
  // scene.add( helper );

  // const d = 4
  // const k = Math.tan(Math.PI*((50/2)/180))*d
  // const geometry = new PlaneGeometry(k*2, k*2);
  // const material = new MeshBasicMaterial({ color: 0xffff00 })
  // const plane = new Mesh(geometry, material);
  // plane.position.set(0, 0, -(d-1))
  //scene.add(plane)

  const rotatingGroup = new Group()
  scene.add(rotatingGroup)

  let pmremGenerator = new PMREMGenerator(renderer);
  pmremGenerator.compileEquirectangularShader()

  new RGBELoader()
    .setDataType(UnsignedByteType)
    .load('venice_sunset_0_5k.hdr', (texture: DataTexture) => {
      const envMap = pmremGenerator.fromEquirectangular(texture).texture;

      scene.environment = envMap
    })

  const gtlfLoader = new GLTFLoader().setPath('neodyme/');
  const count = 80

  var particles: InstancedMesh
  const dummyObject = new Object3D();
  dummyObject.matrixAutoUpdate = false;
  var matrix = new Matrix4();
  var position = new Vector3();
  var quaternion = new Quaternion();
  var scale = new Vector3();
  const distance = 10
  const max = Math.tan(Math.PI * ((50 / 2) / 180)) * distance - 0.5

  const targets: Quaternion[] = []

  const gltf = await gtlfLoader.loadAsync('scene.gltf')

  var gltfBox = new Box3().setFromObject(gltf.scene)
  var gltfSize = new Vector3()
  gltfBox.getSize(gltfSize)

  const f = Math.max(gltfSize.x, gltfSize.y) * 1.5
  gltf.scene.scale.set(1 / f, 1 / f, 1 / f)

  gltfBox = new Box3().setFromObject(gltf.scene)
  gltfBox.getCenter(gltf.scene.position)

  gltf.scene.position.multiplyScalar(-1);

  var pivot = new Group()
  pivot.add(gltf.scene)
  pivot.rotateX(Math.PI / 2)

  rotatingGroup.add(pivot)

  var x: Mesh;

  gltf.scene.traverse(function (child: Object3D) {
    if (child.isMesh) {
      x = child
      const instancedMesh = new InstancedMesh(child.geometry, child.material, count)
      scene.add(instancedMesh)
      for (let i = 0; i < count; i++) {
        dummyObject.position.x = ((0.5 - Math.random()) * 2 * max);
        dummyObject.position.y = ((0.5 - Math.random()) * 2 * max) - max * 3;
        dummyObject.position.z = 1 - distance
        dummyObject.scale.set(1 / (f), 1 / (f), 1 / (f))
        dummyObject.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI)
        var vector = new Vector3(1, 0, 0);
        vector.applyAxisAngle(new Vector3(0, 1, 0), dummyObject.rotation.y);
        vector.applyAxisAngle(new Vector3(0, 0, 1), dummyObject.rotation.z);

        targets.push([vector, dummyObject.rotation.x])
        dummyObject.updateMatrix();
        instancedMesh.setMatrixAt(i, dummyObject.matrix);
      }
      particles = instancedMesh
    }
  })

  var clicktick = 0;
  renderer.domElement.addEventListener("click", (e) => {
    e.preventDefault();
    for (let i = 0; i < count; i++) {
      particles.getMatrixAt(i, matrix)
      matrix.decompose(position, quaternion, scale)

      if (position.y < -max) {
        position.y = (max * 2 - (position.y) % max * 2);
        position.x = ((0.5 - Math.random()) * 2 * max)
      }
      matrix.compose(position, quaternion, scale)
      particles.setMatrixAt(i, matrix)
    }

    particles.instanceMatrix.needsUpdate = true;
    clicktick += 1
  }, true);

  const textureLoader = new TextureLoader()
  const fontGroup = new Group()
  rotatingGroup.add(fontGroup)

  const fontLoader = new FontLoader()
  const font = await fontLoader.loadAsync('fonts/helvetiker_regular.typeface.json')
  const mapTexture = await textureLoader.loadAsync('neodyme/Scene_-_Root_baseColor.png');
  const metalTexture = await textureLoader.loadAsync('neodyme/Scene_-_Root_metallicRoughness.png');

  const materials = new MeshStandardMaterial({
    map: mapTexture,
    metalness: 1,
    // roughnessMap: metalTexture,
    metalnessMap: metalTexture
  });

  const setText = (number: number) => {
    fontGroup.clear()

    if (isNaN(number)) {
      return
    }

    const text = number.toString().padStart(3, "0")

    const textGeo = new TextGeometry(text, {
      font: font,
      size: 0.0251,
      height: 0.02,
      curveSegments: 12,
      bevelEnabled: false,
      bevelSize: 0.001,
      bevelThickness: 0.001,
    });

    textGeo.computeBoundingBox();
    const box = textGeo.boundingBox
    if (!box) {
      return
    }

    const centerOffset = -0.5 * (box.max.x - box.min.x);

    const textMesh = new Mesh(textGeo, materials);
    textMesh.position.x = centerOffset;
    textMesh.position.y = -0.2;
    textMesh.position.z = 0;

    textMesh.rotation.x = 0;
    textMesh.rotation.y = 0;

    fontGroup.add(textMesh)
  }

  addUserSelected((user?: User) => {
    setText(user?.number || NaN)
  })

  let stats: Stats
  if (SHOW_STATS) {
    stats = new Stats();
    document.body.appendChild(stats.dom);

    let gpuPanel = new GPUStatsPanel(renderer.getContext());
    stats.addPanel(gpuPanel);
    stats.showPanel(0);
  }


  rotatingGroup.rotation.y = Math.PI

  const animate = () => {
    requestAnimationFrame(animate);
    let delta = clock.getDelta();

    if (SHOW_STATS) {
      stats.update();
    }

    rotatingGroup.rotateY(1.5 * delta)

    if (particles) {
      for (let i = 0; i < count; i++) {
        particles.getMatrixAt(i, matrix)
        matrix.decompose(position, quaternion, scale)
        position.y -= 12.5 * delta

        targets[i][1] += 0.1

        quaternion.setFromAxisAngle(targets[i][0], targets[i][1])
        matrix.compose(position, quaternion, scale)
        particles.setMatrixAt(i, matrix)
      }

      particles.instanceMatrix.needsUpdate = true;
    }

    rotatingGroup.scale.x = 1 + Math.sin(clicktick) * 0.2
    rotatingGroup.scale.y = 1 + Math.sin(clicktick) * 0.2
    rotatingGroup.scale.z = 1 + Math.sin(clicktick) * 0.2
    clicktick -= 0.1
    if (clicktick < 0) {
      clicktick = 0
    }

    // remove the background
    const initialBackground = scene.background;
    scene.background = null;

    // force the depthMaterial to everything
    scene.overrideMaterial = depthMaterial;

    // render to the render target to get the depths
    renderer.setRenderTarget(renderTarget);
    renderer.render(scene, shadowCamera);

    // and reset the override material
    scene.overrideMaterial = null;

    const blur = 3
    blurShadow(blur);

    // a second pass to reduce the artifacts
    // (0.4 is the minimum blur amout so that the artifacts are gone)
    blurShadow(blur * 0.4);

    // reset and render the normal scene
    renderer.setRenderTarget(null);
    scene.background = initialBackground;

    //renderer.render(scene, camera);
    composer.render();

  }

  window.addEventListener('resize', () => {
    onResizeHead()
    
    const width = elem.clientWidth
    const height = elem.clientHeight

    camera.aspect = width / height;
    camera.updateProjectionMatrix();

    renderer.setSize(width, height);
  })

  const composer = new EffectComposer(renderer);

  const renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);
  const luminosityPass = new ShaderPass(CopyShader);
  composer.addPass(luminosityPass);
  animate();

  const coin_scene = document.getElementById('coin-scene')
  if (coin_scene) {
    coin_scene.style.backgroundImage = "unset"
  }
}

init()

